// frontend/src/components/CalendarPicker.js

import { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { useTheme } from '../contexts/ThemeContext';

const CalendarPicker = ({ canBookRecurring, selectedDates = [], selectedDate, onDateSelect, availableDates, bookingWindowDays, className }) => {
  const { t } = useTranslation();
  // Get formatting functions and locale from enhanced hook
  const { formatDate, formatDateISO, currentLocale } = useDateTimePreferences();
  const { components, text, border, background } = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);

  // Initialize current month based on first available date if no date is selected
  useEffect(() => {
    if (!selectedDate ) {
      const today = new Date();
      today.setHours(0,0,0,0);
      setCurrentMonth(today);
    }
  }, [availableDates, selectedDate]);

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const startOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };


  // Helper function to parse date strings into local Date objects
  const isDateAvailable = (date) => {
    if (!availableDates?.length) return false;
    const dateString = formatDateISO(new Date(date));
    return availableDates.includes(dateString);
  };
  
  const isToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate.getTime() === today.getTime();
  };
  
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const handleDateClick = (date) => {
    const newDate = new Date(date);
    if (!isPastDate(newDate) && isDateAvailable(newDate)) {
      if (canBookRecurring) {
        const dateString = formatDateISO(newDate);
        // Remove duplicates and ensure dates are unique
        const existingIndex = selectedDates.indexOf(dateString);
        const newSelectedDates = existingIndex >= 0
          ? selectedDates.filter((_, index) => index !== existingIndex)
          : [...new Set([...selectedDates, dateString])].sort();
        onDateSelect(newSelectedDates);
      } else {
        onDateSelect(newDate);
        setShowCalendar(false);
      }
    }
  };

  const isSelected = (date) => {
    const compareDate = formatDateISO(new Date(date));
    return canBookRecurring 
      ? selectedDates.includes(compareDate)
      : formatDateISO(selectedDate) === compareDate;
  };


  const buttonContent = () => {
    if (canBookRecurring) {
      if (!selectedDates.length) return t('selectDateNextDays', { days: bookingWindowDays });
      
      return (selectedDates.length === 1)? formatDate(new Date(selectedDates[0] + 'T00:00:00')) : t('{{count}} dates selected', { count: selectedDates.length });
    }
    return selectedDate ? formatDate(new Date(selectedDate)) : t('Select a date');
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const startDay = startOfMonth(currentMonth);

    // Add empty cells for days before the start of the month
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" />);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isDisabled = isPastDate(date) || !isDateAvailable(date);

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !isDisabled && handleDateClick(date)}
          disabled={isDisabled}
          className={`
            h-8 w-8 rounded-full flex items-center justify-center text-sm relative
            ${isDisabled ? `${text.disabled} cursor-not-allowed` : 'hover:bg-blue-100 dark:hover:bg-blue-900'}
            ${isToday(date) ? 'border border-blue-500' : ''}
            ${isSelected(date) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
            ${!isDisabled && !isSelected(date) ? 'hover:bg-blue-100 dark:hover:bg-blue-900' : ''}
            ${isDateAvailable(date) && !isSelected(date) ? 'font-medium text-blue-600 dark:text-blue-400' : ''}
          `}
          title={isDisabled ? t('This date is not available') : ''}
        >
          {day}
          {isDateAvailable(date) && !isSelected(date) && !isDisabled && (
            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full"></span>
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setShowCalendar(!showCalendar)}
        className={`w-full p-2 border rounded-md flex items-center justify-between ${background.elevated} ${text.secondary} ${border.strong}`}
      >
        <span className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-blue-500" />
          {buttonContent()}
        </span>
        <ChevronRight
          className={`w-4 h-4 transform transition-transform ${showCalendar ? 'rotate-90' : ''}`}
        />
      </button>

      {showCalendar && (
        <div className={`absolute top-full left-0 z-50 mt-2 w-64 rounded-lg shadow-lg p-4 ${components.dropdown.base}`}>
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={() =>
                setCurrentMonth(
                  new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
                )
              }
              className={`p-1 rounded ${components.navigation.item}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className={`font-semibold ${text.primary}`}>
              {currentMonth.toLocaleDateString(currentLocale, { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentMonth(
                  new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                )
              }
              className={`p-1 rounded ${components.navigation.item}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className={`h-8 flex items-center justify-center text-xs ${text.muted}`}>
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>

          <div className={`mt-3 pt-3 border-t text-xs ${border.default} ${text.muted}`}>
            <div className="flex items-center mb-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              <span>{t('availableDates')}</span>
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
              <span>{t('unavailableDates')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPicker;
